import React, {Fragment, useState, useEffect, useContext} from "react";

import UsersTable from "root/Tables/UsersTable";
import ChildrenTable from "root/Tables/ChildrenTable";
import ArchivedUsersTable from "root/Tables/ArchivedUsersTable";
import BlockedUsersTable from "root/Tables/BlockedUsersTable";
import './UsersTab.scss';

import useModalAddUser from "root/Modals/AddUser/useModalAddUser";
import useModalUpdateChild from "root/Modals/UpdateChildDetails/useModalUpdateChild";
import useModalChangePassword from "root/Modals/ChangePassword/useModalChangePassword";
import useModalManageRights from "root/Modals/ManageRights/useModalManageRights";
import {suspendUser, reinstateUser, inviteAgain, getMyUsersInOrg, archiveUser, getBlockedUsersInOrg, unblockUser} from "root/DataFunctions/UserDataFns";
import {getDataFromResponse} from "root/DataFunctions/BaseDataFns";
import {
    SafeguardingFilters,
    UserStatus,
    UserTypeFilters,
    UserTypes,
    UserTypeTitles
} from "root/DataFunctions/Constants";
// import bootbox from "bootbox";
import {useParams} from "react-router";
import {useLocation} from "react-router-dom";
import {useGlobalContext} from "root/contexts/GlobalContext";

const UsersTab = (props) =>
{
    const [data, setData] = useState({isLoading: true, data: []});

    const { user_org } = useGlobalContext() ;

    // Set the user's current organisation - this allows us to change it using a dropdown when we get around to
    // allowing users to select multiple organisations
    const [selectedOrg, setSelectedOrg] = useState ( user_org ) ;
    const [userTypeName, setUserTypeName] = useState('');
    const [userTypeId, setUserTypeId] = useState('');
    const {pathname}  = useLocation();

    const [renderAddUserModal, showAddUserModal] = useModalAddUser((result) =>
    {
        if (result === 'saved')
        {
            getData();
        }
    })

    const [renderAddChildModal, showAddChildModal] = useModalUpdateChild((result) =>
    {
        getData();
    })

    const [renderChangePasswordModal, showChangePasswordModal] = useModalChangePassword(null, (result) =>
    {
        // do nothing
    })

    const [renderManageRightsModal, showManageRightsModal] = useModalManageRights((result) =>
    {
        // do nothing
    })

    const {user_type} = useParams();
    const userFilter = user_type === undefined ? UserTypeFilters.STAFF : user_type;

    useEffect(() => {
        getData();
    }, [userFilter]);

    const getData = () => {
        const userFilter = user_type === undefined ? UserTypeFilters.STAFF : user_type;
        let userTypeId = 0;
        let userStatus = null;

        switch (userFilter)
        {
            case UserTypeFilters.STAFF :
                setUserTypeName(UserTypeTitles.STAFF);
                userTypeId = UserTypeFilters.STAFF; //This is just so we can identify when we want to show staff type dropdown
                break;

            case UserTypeFilters.CHILDREN :
                setUserTypeName(UserTypeTitles.CHILDREN);
                userTypeId = UserTypes.CHILD;
                break;

            case UserTypeFilters.FOSTER_CARERS:
                setUserTypeName(UserTypeTitles.FOSTER_CARERS);
                userTypeId = UserTypes.FOSTER_PARENT;
                break;

            case UserTypeFilters.PROFESSIONALS:
                setUserTypeName(UserTypeTitles.PROFESSIONALS);
                userTypeId = UserTypes.PROFESSIONAL;
                break;

            case UserTypeFilters.OTHERS:
                setUserTypeName(UserTypeTitles.OTHERS);
                userTypeId = UserTypes.OTHER;
                break;

            case UserTypeFilters.FOREVER_PARENTS:
                setUserTypeName(UserTypeTitles.FOREVER_PARENTS);
                userTypeId = UserTypes.FOREVER_PARENT;
                break;

            case UserTypeFilters.CONNECTED_CARERS:
                setUserTypeName(UserTypeTitles.CONNECTED_CARERS);
                userTypeId = UserTypes.CONNECTED_CARER;
                break;

            case UserTypeFilters.BIRTH_PARENTS:
                setUserTypeName(UserTypeTitles.BIRTH_PARENTS);
                userTypeId = UserTypes.BIRTH_PARENT;
                break;

            case UserTypeFilters.ARCHIVED:
                setUserTypeName(UserTypeTitles.ARCHIVED);
                userTypeId = UserTypes.ALL;
                userStatus = UserStatus.ARCHIVED;
                break;

            case UserTypeFilters.BLOCKED:
                setUserTypeName(UserTypeTitles.BLOCKED);
                userTypeId = UserTypes.ALL;
                break;

            default:
                setUserTypeName(UserTypeTitles.STAFF);
                userTypeId = UserTypeFilters.STAFF;
                break;
        }

        //We can come here from safeguarding or users
        if(user_type === UserTypeFilters.BLOCKED || pathname.includes(SafeguardingFilters.BLOCKED_USERS)){
            getBlockedUsersInOrg( selectedOrg, (response) =>
            {
                setData({isLoading: false, data: getDataFromResponse(response)});
                setUserTypeId(UserTypes.ALL);
            });
        }
        else{
            getMyUsersInOrg( selectedOrg, userTypeId, userStatus, (response) =>
            {
                setData({isLoading: false, data: getDataFromResponse(response)});
                setUserTypeId(userTypeId);
            });
        }

    };

    const clickHandler = (user_id, mode, userTypeId, name, blocked_by) =>
    {
        switch (mode)
        {
            case 'create':
            case 'edit':
            case 'view':
                if (user_type === UserTypeFilters.CHILDREN)
                {
                    showAddChildModal(user_id, mode);
                }
                else
                {
                    showAddUserModal(user_id, mode, userTypeId, false);
                }

                break;

            case 'suspend' :
                bootbox.confirm({
                    size: "small",
                    message: "Are you sure you want to suspend this user?",
                    callback: (result =>
                    {
                        if (result)
                        {
                            doSuspend(user_id);
                        }
                    })
                })
                break;

            case 'activate' :
                bootbox.confirm({
                    size: "small",
                    message: "Are you sure you want to activate this user?",
                    callback: (result =>
                    {
                        if (result)
                        {
                            doActivate(user_id);
                        }
                    })
                })
                break;

            case 'delete' :
                bootbox.confirm({
                    size: "small",
                    message: "Are you sure you want to archive this user? All their connections will be removed.",
                    callback: (result =>
                    {
                        if (result)
                        {
                            doArchive(user_id);
                        }
                    })
                })
                break;

            case 'invite' :
                doInviteAgain(user_id);
                break;

            case 'password' :
                showChangePasswordModal(user_id);
                break;

            case 'stats' :
                showChangePasswordModal(user_id);
                break;

            case 'rights' :
                showManageRightsModal(user_id, name);
                break;

            case 'unblock' :
                bootbox.confirm({
                    size: "small",
                    message: "Are you sure you want to un-block this user? You must be sure the user who blocked them is ok with this. " +
                        "<span style='color: red;'>You will also need to re-instate the connection between the two users.</span>",
                    callback: (result =>
                    {
                        if (result)
                        {
                            doUnblock(user_id, blocked_by);
                        }
                    })
                })
                break;
        }
    }

    const doSuspend = (user_id) =>
    {
        suspendUser(user_id, getData);
    }

    const doActivate = (user_id) =>
    {
        reinstateUser(user_id, getData);
    }

    const doArchive = (user_id) =>
    {
        archiveUser(user_id, getData);
    }

    const doInviteAgain = (user_id) =>
    {
        inviteAgain(user_id, confirmInvite);
    }

    const confirmInvite = () =>
    {
        bootbox.alert({
            size: "small",
            message: "A new invite has been sent to this user.",
            callback: function ()
            {
                getData();
            }
        })
    }

    const doUnblock = (user_id, blocked_by) =>
    {
        unblockUser(user_id, blocked_by);
    }

    const myUsersTable = () => {
        const displayBlockedUsers = user_type === UserTypeFilters.BLOCKED || pathname.includes(SafeguardingFilters.BLOCKED_USERS);

        return (
            <Fragment>
                <div className="post-body">
                    {user_type !== UserTypeFilters.CHILDREN && user_type !== UserTypeFilters.ARCHIVED && !displayBlockedUsers &&
                        <UsersTable loading={data.isLoading} data={data.data} clickHandler={clickHandler}/>}
                    {user_type === UserTypeFilters.CHILDREN && <ChildrenTable loading={data.isLoading} data={data.data} clickHandler={clickHandler}/>}
                    {user_type === UserTypeFilters.ARCHIVED && <ArchivedUsersTable loading={data.isLoading} data={data.data} clickHandler={clickHandler}/>}
                    {displayBlockedUsers && <BlockedUsersTable loading={data.isLoading} data={data.data} clickHandler={clickHandler}/>}
                </div>

                {renderAddUserModal(props)}
                {renderAddChildModal(props)}
                {renderChangePasswordModal(props)}
                {renderManageRightsModal(props)}
            </Fragment>
        )
    }

    return (
        <Fragment>
            {myUsersTable()}
        </Fragment>
    )
}

UsersTab.defaultProps = {};

export default UsersTab;

